import type { Editor } from '@tiptap/react'
import { BubbleMenu } from '@tiptap/react/menus'
import {
    Bold,
    Braces,
    Italic,
    Link2,
    Link2Off,
    Strikethrough,
    Underline,
    type LucideIcon,
} from 'lucide-react'
import { useState } from 'react'

import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'

import type { EmailVariable } from '../types'

interface Props {
    editor: Editor | null
    variables: EmailVariable[]
}

/**
 * Compact floating toolbar shown when text is selected.
 * Inspired by Maily's bubble menu but trimmed to the essentials merchants
 * need: bold / italic / underline / strike / link / variable.
 */
export function EmailBubbleMenu({ editor, variables }: Props) {
    const { t } = useTranslation('emails')
    if (!editor) return null

    return (
        <BubbleMenu
            editor={editor}
            options={{ placement: 'top' }}
            shouldShow={({ editor, from, to, state }) => {
                if (from === to) return false
                if (editor.isActive('emailButton')) return false
                if (editor.isActive('emailSpacer')) return false
                if (editor.isActive('emailVariable')) return false
                if (editor.isActive('image')) return false
                // Only show when the selection is in inline content
                const { doc } = state
                const node = doc.nodeAt(from)
                if (node?.isBlock) return false
                return true
            }}
            className="flex items-center gap-0.5 rounded-lg border bg-popover p-0.5 shadow-lg"
        >
            <ToolbarButton
                icon={Bold}
                active={editor.isActive('bold')}
                onClick={() => editor.chain().focus().toggleBold().run()}
                label={`${t('builder.bubbleMenu.bold')} (⌘B)`}
            />
            <ToolbarButton
                icon={Italic}
                active={editor.isActive('italic')}
                onClick={() => editor.chain().focus().toggleItalic().run()}
                label={`${t('builder.bubbleMenu.italic')} (⌘I)`}
            />
            <ToolbarButton
                icon={Underline}
                active={editor.isActive('underline')}
                onClick={() => editor.chain().focus().toggleUnderline().run()}
                label={`${t('builder.bubbleMenu.underline')} (⌘U)`}
            />
            <ToolbarButton
                icon={Strikethrough}
                active={editor.isActive('strike')}
                onClick={() => editor.chain().focus().toggleStrike().run()}
                label={t('builder.bubbleMenu.strikethrough')}
            />
            <Divider />
            <LinkButton editor={editor} />
            <VariableQuickInsert editor={editor} variables={variables} />
        </BubbleMenu>
    )
}

function ToolbarButton(props: {
    icon: LucideIcon
    active?: boolean
    onClick: () => void
    label: string
}) {
    const { icon: Icon, active, onClick, label } = props
    return (
        <button
            type="button"
            onClick={onClick}
            title={label}
            aria-label={label}
            className={cn(
                'flex h-7 w-7 items-center justify-center rounded-md transition-colors',
                active
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
        >
            <Icon className="h-3.5 w-3.5" />
        </button>
    )
}

function Divider() {
    return <div className="mx-0.5 h-5 w-px bg-border" />
}

function LinkButton({ editor }: { editor: Editor }) {
    const { t } = useTranslation('emails')
    const active = editor.isActive('link')
    const existing = (editor.getAttributes('link').href as string) || ''
    const [url, setUrl] = useState(existing)
    const [open, setOpen] = useState(false)

    const commit = () => {
        const finalUrl = url.trim()
        if (!finalUrl) {
            editor.chain().focus().extendMarkRange('link').unsetLink().run()
        } else {
            editor
                .chain()
                .focus()
                .extendMarkRange('link')
                .setLink({ href: ensureProtocol(finalUrl) })
                .run()
        }
        setOpen(false)
    }

    return (
        <Popover
            open={open}
            onOpenChange={(o) => {
                setOpen(o)
                if (o) setUrl(existing)
            }}
        >
            <PopoverTrigger asChild>
                <button
                    type="button"
                    title={`${t('builder.bubbleMenu.link')} (⌘K)`}
                    className={cn(
                        'flex h-7 w-7 items-center justify-center rounded-md transition-colors',
                        active
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                    )}
                >
                    <Link2 className="h-3.5 w-3.5" />
                </button>
            </PopoverTrigger>
            <PopoverContent align="center" side="top" className="w-72 space-y-2 p-2">
                <Input
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault()
                            commit()
                        }
                    }}
                    placeholder="https://..."
                    autoFocus
                    className="h-8 font-mono text-xs"
                />
                <div className="flex items-center justify-between">
                    <button
                        type="button"
                        onClick={commit}
                        className="rounded-md bg-primary px-3 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90"
                    >
                        {t('builder.bubbleMenu.saveLink')}
                    </button>
                    {active && (
                        <button
                            type="button"
                            onClick={() => {
                                editor
                                    .chain()
                                    .focus()
                                    .extendMarkRange('link')
                                    .unsetLink()
                                    .run()
                                setOpen(false)
                            }}
                            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                        >
                            <Link2Off className="h-3 w-3" /> {t('builder.bubbleMenu.removeLink')}
                        </button>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    )
}

function VariableQuickInsert({
    editor,
    variables,
}: {
    editor: Editor
    variables: EmailVariable[]
}) {
    const { t } = useTranslation('emails')
    const [open, setOpen] = useState(false)
    if (variables.length === 0) return null

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    title={t('builder.bubbleMenu.insertVariable')}
                    className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                    <Braces className="h-3.5 w-3.5" />
                </button>
            </PopoverTrigger>
            <PopoverContent align="end" side="top" className="w-64 p-1">
                <div className="border-b px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    {t('builder.bubbleMenu.insertVariable')}
                </div>
                <div className="max-h-56 overflow-y-auto py-1">
                    {variables.map((v) => (
                        <button
                            key={v.key}
                            type="button"
                            onClick={() => {
                                editor.chain().focus().insertVariable(v).run()
                                setOpen(false)
                            }}
                            className="flex w-full items-center gap-2 rounded-md px-2 py-1 text-left hover:bg-muted"
                        >
                            <Braces className="h-3 w-3 shrink-0 text-rose-600" />
                            <span className="truncate font-mono text-xs">
                                {v.label || v.key}
                            </span>
                        </button>
                    ))}
                </div>
            </PopoverContent>
        </Popover>
    )
}

function ensureProtocol(url: string): string {
    if (/^https?:\/\//i.test(url)) return url
    if (/^mailto:|^tel:/i.test(url)) return url
    if (url.startsWith('#') || url.startsWith('/')) return url
    // Allow `{{ var }}` expressions
    if (url.trim().startsWith('{{')) return url
    return `https://${url}`
}
